import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LabelList, ResponsiveContainer,
} from 'recharts';
import { AnalyticsPeriod, PeriodKey } from '../../../../hooks/useBoardAnalytics';
import DateRangePills from './DateRangePills';

interface JobsOverTimeChartProps {
  periods: Record<PeriodKey, AnalyticsPeriod>;
  showValues?: boolean;
}

function formatPeriodLabel(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}>
      <div style={{ color: '#6b7280', marginBottom: 2 }}>{formatPeriodLabel(label)}</div>
      <div style={{ fontWeight: 600, color: '#111827' }}>{payload[0].value} jobs</div>
    </div>
  );
}

export default function JobsOverTimeChart({ periods, showValues = true }: JobsOverTimeChartProps) {
  const [period, setPeriod] = useState<PeriodKey>('30d');
  const raw = periods[period]?.jobs_over_time ?? [];
  const data = raw.map((d) => ({ ...d, label: d.period }));

  return (
    <div className="metrics-card">
      <div className="metrics-card__header">
        <span className="metrics-card__title">Jobs Added Over Time</span>
        <DateRangePills value={period} onChange={setPeriod} />
      </div>
      {data.length === 0 ? (
        <div className="metrics-empty">No data yet</div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={data} margin={{ top: 0, right: 8, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
            <XAxis
              dataKey="label"
              tickFormatter={formatPeriodLabel}
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f9fafb' }} />
            <Bar dataKey="count" fill="#374151" radius={[4, 4, 0, 0]}>
              {showValues && (
                <LabelList dataKey="count" position="top" fontSize={10} fill="#6b7280" />
              )}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
